import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Wrapper, StyledPlayIcon, StyledPauseIcon,
  StyledTrack,
} from './styles';

class AudioPlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPlaying: false,
      trackValue: 0,
      activeSource: props.src || '',
    };
    this.audioRef = React.createRef();
    this.trackRef = React.createRef();
    this.trackClickHandler = null;
    this.timeUpdateHandler = null;
    this.playHandler = null;
    this.pauseHandler = null;
    this.canPlayHandler = null;
  }

  componentDidMount() {
    const { getAudioRef } = this.props;
    const audioElement = this.audioRef.current;
    const trackElement = this.trackRef.current;
    if (audioElement && trackElement) {
      trackElement.value = 0;

      this.canPlayHandler = () => {
        if (typeof getAudioRef === 'function') {
          getAudioRef(audioElement);
        }
        trackElement.max = parseInt(audioElement.duration, 10) || 0;
      };
      audioElement.addEventListener('canplay', this.canPlayHandler);

      this.trackClickHandler = () => {
        const nextValue = Number(trackElement.value) || 0;
        audioElement.currentTime = nextValue;
        const { trackValue } = this.state;
        if (trackValue !== nextValue) {
          this.setState({ trackValue: nextValue });
        }
      };
      trackElement.addEventListener('click', this.trackClickHandler);

      this.timeUpdateHandler = () => {
        const currentTime = parseInt(audioElement.currentTime, 10) || 0;
        trackElement.value = currentTime;
        const { trackValue } = this.state;
        if (trackValue !== currentTime) {
          this.setState({ trackValue: currentTime });
        }
      };
      audioElement.addEventListener('timeupdate', this.timeUpdateHandler);

      this.playHandler = () => this.setState({ isPlaying: true });
      this.pauseHandler = () => this.setState({ isPlaying: false });
      audioElement.addEventListener('play', this.playHandler);
      audioElement.addEventListener('pause', this.pauseHandler);
    }
  }

  componentDidUpdate(prevProps) {
    const { src, sourceKey } = this.props;
    const { isPlaying } = this.state;

    const prevIdentity = prevProps.sourceKey || prevProps.src;
    const nextIdentity = sourceKey || src;

    if (prevIdentity === nextIdentity) {
      return;
    }

    const audioElement = this.audioRef.current;
    const trackElement = this.trackRef.current;

    this.setState({
      activeSource: src || '',
      trackValue: 0,
      isPlaying: false,
    });

    if (audioElement && trackElement) {
      if (isPlaying) {
        audioElement.pause();
      }
      audioElement.currentTime = 0;
      trackElement.value = 0;
    }
  }

  componentWillUnmount() {
    const audioElement = this.audioRef.current;
    const trackElement = this.trackRef.current;

    if (audioElement) {
      if (this.canPlayHandler) audioElement.removeEventListener('canplay', this.canPlayHandler);
      if (this.timeUpdateHandler) audioElement.removeEventListener('timeupdate', this.timeUpdateHandler);
      if (this.playHandler) audioElement.removeEventListener('play', this.playHandler);
      if (this.pauseHandler) audioElement.removeEventListener('pause', this.pauseHandler);
    }

    if (trackElement && this.trackClickHandler) {
      trackElement.removeEventListener('click', this.trackClickHandler);
    }
  }

  clickHandler = () => {
    const audioElement = this.audioRef.current;
    if (!audioElement) {
      return;
    }

    const { isPlaying } = this.state;
    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
  }

  render() {
    const { className } = this.props;
    const { isPlaying, trackValue, activeSource } = this.state;
    const Button = isPlaying ? StyledPauseIcon : StyledPlayIcon;
    const trackElement = this.trackRef.current;
    const position = trackElement && trackElement.max ? (trackValue / trackElement.max) * 100 : 0;
    return (
      <Wrapper className={className || 'audioplayer'}>
        <Button onClick={this.clickHandler} className="audioplayer-button" />
        <StyledTrack
          type="range"
          step="1"
          ref={this.trackRef}
          position={position}
          className="audioplayer-track"
        />
        <audio src={activeSource} ref={this.audioRef} className="audioplayer-source" />
      </Wrapper>
    );
  }
}

AudioPlayer.propTypes = {
  src: PropTypes.string,
  sourceKey: PropTypes.string,
  getAudioRef: PropTypes.func,
  className: PropTypes.string,
};

export default AudioPlayer;
